import { getCsrfToken } from 'saddlebag-user-management';

import { SUBSCRIPTION_STORE_ENDPOINT } from './config';

import type { SubscriptionClientInterface } from '@web-platform/global-components-marketing-consent/types';

/**
 * This is the implementation for the two subscription methods needed to check if the user is already subscribed to marketing emails and to update a user;'s marketing preferences if they are not.
 *
 * @example
 * import { SubscriptionClient } from '@skyscanner-internal/global-component/login-form/helpers';
 *
 * const endpoint = !isProductionEnv && 'http://localhost:3000/mock-subscription-store';
 * const subscriptionClient = new SubscriptionClient(endpoint);
 */
class SubscriptionClient implements SubscriptionClientInterface {
  /**
   * Override the default subscription endpoint to retrieve and update subscriptions
   */
  subscriptionStoreEndpoint: string;

  /**
   * Initialize constructor with optional API endpoint to override the default value
   * @param endpoint
   */
  constructor(endpoint: string = SUBSCRIPTION_STORE_ENDPOINT) {
    this.subscriptionStoreEndpoint = endpoint;
  }

  private static handleError = (res: Response) => {
    throw new Error(`Received unhandled status: ${res.status} from Subscription Store`);
  };

  private static getRequestHeaders = () => ({
    'x-csrf-token': getCsrfToken(),
    'Content-Type': 'application/json; charset=utf-8',
  });

  isSubscribed = async (): Promise<boolean> => {
    const res = await fetch(this.subscriptionStoreEndpoint, {
      method: 'GET',
      headers: SubscriptionClient.getRequestHeaders(),
      credentials: 'include',
    });
    if (res.ok) {
      const { email } = await res.json();
      return email ? email.enabled : false;
    }
    // when the user is not subscribed to marketing comms subs store returns a 404
    if (res.status === 404) {
      return false;
    }
    return SubscriptionClient.handleError(res);
  };

  updateSubscription = async (
    enabled: boolean,
    locale: string,
    market: string,
    emailSubscriptionSource?: string,
  ): Promise<void> => {
    const res = await fetch(this.subscriptionStoreEndpoint, {
      method: 'PUT',
      body: JSON.stringify({
        email: {
          enabled,
          source: emailSubscriptionSource || 'login-form',
          channel: 'web',
        },
        culture: {
          market: market.toLowerCase(),
          locale: locale.toLowerCase(),
        },
      }),
      headers: SubscriptionClient.getRequestHeaders(),
      credentials: 'include',
    });
    if (!res.ok) {
      SubscriptionClient.handleError(res);
    }
  };
}

export default SubscriptionClient;
